import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';

// Liens vers les PDF hébergés localement
const PDF_PATHS = {
  en: "/assets/pdfs/documentation-en.pdf",
  zh: "/assets/pdfs/documentation-zh.pdf"
};

const DocumentationViewContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px 5% 150px; /* Augmenté l'espace en bas pour la navbar */
  position: relative;
`;

const Title = styled(motion.h1)`
  font-size: clamp(1.5rem, 5vw, 2.5rem);
  font-weight: 600;
  color: #fff;
  margin-bottom: 30px;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 2px;
  }
`;

const PDFContainer = styled(motion.div)`
  width: 90%;
  max-width: 1000px;
  height: 65vh; /* Légèrement réduit pour éviter les chevauchements */
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  background: #fff;
  position: relative;
  margin-bottom: 40px; /* Augmenté l'espace entre le PDF et les boutons */
`;

// Conteneur PDF sans zoom excessif
const PDFFrame = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 50px; /* Ajout d'une marge en bas des boutons */
  position: relative;
  z-index: 10; /* Pour s'assurer que les boutons restent cliquables */
`;

const ActionButton = styled(motion.button)`
  padding: 10px 20px;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 30px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-2px);
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  z-index: 2;
`;

const Spinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid white;
  width: 40px;
  height: 40px;
  margin-bottom: 15px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  margin-top: 10px;
  font-size: 1rem;
  opacity: 0.9;
`;

const DocumentationView = () => {
  const { lang } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const pdfPath = PDF_PATHS[lang] || PDF_PATHS.en;
  
  // Titre de la page basé sur la langue
  const pageTitle = lang === 'zh' ? '文档' : 'Documentation';

  useEffect(() => {
    // Définir un temps de chargement minimum pour une meilleure expérience utilisateur
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    
    // Forcer le scroll en haut au chargement de la page
    window.scrollTo(0, 0);
    
    // Ajouter un peu d'espace en bas en modifiant temporairement le padding du body
    document.body.style.paddingBottom = '150px';
    
    return () => {
      clearTimeout(timer);
      // Restaurer le padding original
      document.body.style.paddingBottom = '';
    };
  }, []);

  const handleBackClick = () => {
    navigate('/documentation');
  };

  return (
    <PageLayout>
      <DocumentationViewContainer>
        <Title
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {pageTitle}
        </Title>
        
        <PDFContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {loading && (
            <LoadingOverlay>
              <Spinner />
              <LoadingText>
                {lang === 'zh' ? '加载文档中...' : 'Loading document...'}
              </LoadingText>
            </LoadingOverlay>
          )}
          <PDFFrame>
            <iframe 
              src={pdfPath}
              title={`Documentation PDF - ${lang.toUpperCase()}`}
              onLoad={() => setLoading(false)}
              frameBorder="0"
            />
          </PDFFrame>
        </PDFContainer>
        
        <ButtonContainer>
          <ActionButton
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            onClick={handleBackClick}
          >
            ← {lang === 'zh' ? '返回选择' : 'Back to Selection'}
          </ActionButton>
          
          <ActionButton
            as="a"
            href={pdfPath}
            download={lang === 'zh' ? "INO-Studio-文档.pdf" : "INO-Studio-Documentation.pdf"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {lang === 'zh' ? '下载 PDF ↓' : 'Download PDF ↓'}
          </ActionButton>
        </ButtonContainer>
      </DocumentationViewContainer>
    </PageLayout>
  );
};

export default DocumentationView;